import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode, type Ref } from "react";
import { Combobox } from "@base-ui/react/combobox";
import { C, FONT_TEXT } from "../tokens";
import { NO_AUTOFILL } from "../lib/noAutofill";

export interface FieldComboboxOption {
  value: string;
  label: string;
  detail?: string;
  icon?: ReactNode;
  badge?: ReactNode;
}
type Row = FieldComboboxOption & { kind: "option" | "clear" | "create" };

/** shadcn's Base UI popup combobox composition, using the console's tokens.
 * Base UI owns focus, arrow navigation, selection and dismissal. */
export function FieldCombobox({
  label, value, options, onChange, children, open: controlledOpen, onOpenChange,
  onRequestOptions, onCreate, createOption, loading = false, error, emptyText = "No choices available",
  clearLabel, triggerTestId, panelTestId, inputTestId, optionTestId, emptyTestId,
  triggerStyle, title, align = "end", showIndicator = true,
  query: controlledQuery, onQueryChange, filterOptions = true, footer, triggerLabel, triggerRef, restoreFocus = true,
}: {
  label: string;
  value: string;
  options: FieldComboboxOption[];
  onChange: (value: string | null) => void;
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onRequestOptions?: () => void;
  onCreate?: (value: string) => void;
  createOption?: (value: string) => Pick<FieldComboboxOption, "label" | "icon">;
  loading?: boolean;
  error?: string;
  emptyText?: string;
  clearLabel?: string;
  triggerTestId?: string;
  panelTestId?: string;
  inputTestId?: string;
  optionTestId?: string;
  emptyTestId?: string;
  triggerStyle?: CSSProperties;
  showIndicator?: boolean;
  title?: string;
  align?: "start" | "end";
  /** Remote directories own their query and filtering. */
  query?: string;
  onQueryChange?: (query: string) => void;
  filterOptions?: boolean;
  footer?: ReactNode;
  triggerLabel?: string;
  triggerRef?: Ref<HTMLButtonElement>;
  restoreFocus?: boolean;
}) {
  const [localOpen, setLocalOpen] = useState(false);
  const [localQuery, setLocalQuery] = useState("");
  const query = controlledQuery ?? localQuery;
  const setQuery = (next: string) => { setLocalQuery(next); onQueryChange?.(next); };
  const open = controlledOpen ?? localOpen;
  const inputRef = useRef<HTMLInputElement>(null);
  const requestRef = useRef(onRequestOptions);
  requestRef.current = onRequestOptions;
  useEffect(() => {
    if (open) requestRef.current?.();
    else setLocalQuery("");
  }, [open]);
  const rows = useMemo<Row[]>(() => options.map(option => ({ ...option, kind: "option" })), [options]);
  const text = query.trim();
  const matches = filterOptions ? rows.filter(option => [option.label, option.detail].some(part => part?.toLocaleLowerCase().includes(text.toLocaleLowerCase()))) : rows;
  const visible: Row[] = loading || error ? [] : [...matches];
  if (!text && clearLabel) visible.unshift({ value: "", label: clearLabel, kind: "clear" });
  const canCreate = text && onCreate && !loading && !matches.length;
  if (canCreate) {
    visible.push({ value: text, label: `Use “${text}”`, ...createOption?.(text), kind: "create" });
  }
  const selected = rows.find(option => option.value === value) ?? null;
  const note = loading ? "Loading choices…" : error || (!matches.length && !canCreate ? (options.length ? "No matching choices" : emptyText) : undefined);

  return <Combobox.Root<Row>
    items={visible} filter={null} value={selected} inputValue={query} open={open} autoHighlight
    isItemEqualToValue={(a, b) => a.kind === b.kind && a.value === b.value}
    itemToStringLabel={option => option.label}
    onInputValueChange={setQuery}
    onOpenChange={next => { setLocalOpen(next); onOpenChange?.(next); if (!next) setQuery(""); }}
    onValueChange={next => {
      if (!next) return;
      if (next.kind === "create") onCreate?.(next.value);
      else onChange(next.kind === "clear" ? null : next.value);
    }}
  >
    <Combobox.Trigger ref={triggerRef} aria-label={triggerLabel ?? label} data-testid={triggerTestId} title={title} className="kb"
      style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 7, width: "100%", minWidth: 0,
        padding: "5px 7px", border: "1px solid transparent", borderRadius: 8, background: "transparent", color: C.ink,
        fontFamily: FONT_TEXT, fontSize: 16.67, fontWeight: 650, lineHeight: 1.45, textAlign: "right", cursor: "pointer", ...triggerStyle }}>
      {children}
      {showIndicator && <Combobox.Icon style={{ display: "flex", color: C.sub, flexShrink: 0 }}>
        <svg width="14" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="m5 6 3-3 3 3m-6 4 3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Combobox.Icon>}
    </Combobox.Trigger>
    <Combobox.Portal>
      <Combobox.Positioner align={align} sideOffset={7} style={{ zIndex: 60 }}>
        <Combobox.Popup aria-label={`${label} options`} data-testid={panelTestId} initialFocus={inputRef} finalFocus={restoreFocus}
          style={{ boxSizing: "border-box", width: "min(300px, calc(100vw - 24px))", maxHeight: "var(--available-height)", display: "flex", flexDirection: "column",
            padding: 5, border: `1px solid ${C.line2}`, borderRadius: 12, background: C.surface, color: C.ink,
            boxShadow: `0 14px 30px ${C.popShadow}`, fontFamily: FONT_TEXT, outline: "none" }}>
          <Combobox.Input ref={inputRef} aria-label={`Search ${label.toLowerCase()}`} placeholder={`Search ${label.toLowerCase()}…`}
            data-testid={inputTestId} {...NO_AUTOFILL}
            style={{ boxSizing: "border-box", width: "100%", flexShrink: 0, padding: "9px 10px", marginBottom: 4,
              border: "none", borderBottom: `1px solid ${C.line}`, background: "transparent", color: C.ink, outline: "none", font: "inherit", fontSize: 15.52 }} />
          {note && <Combobox.Status data-testid={emptyTestId} style={{ padding: "8px 10px", fontSize: 13.8, lineHeight: 1.45, color: error ? C.red : C.sub }}>{note}</Combobox.Status>}
          <Combobox.List style={{ minHeight: 0, maxHeight: 300, overflowY: "auto", overscrollBehavior: "contain", scrollPaddingBlock: 5 }}>
            {(option: Row) => <Combobox.Item key={`${option.kind}:${option.value}`} value={option}
              data-testid={optionTestId} data-checked={option.kind === "option" && option.value === value ? "true" : "false"}
              style={state => ({ display: "flex", alignItems: "center", gap: 8, minHeight: 36, padding: "7px 10px", borderRadius: 7,
                cursor: "pointer", userSelect: "none", outline: "none", fontSize: 15.52, lineHeight: 1.4,
                background: state.highlighted ? C.faint : state.selected ? C.hairRow : "transparent" })}>
              {option.icon && <span aria-hidden style={{ display: "flex", flexShrink: 0 }}>{option.icon}</span>}
              <span style={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
                <span>{option.label}</span>
                {option.detail && <span style={{ display: "block", color: C.sub, fontSize: 12.65 }}>{option.detail}</span>}
              </span>
              {option.badge}
              <Combobox.ItemIndicator style={{ display: "flex", flexShrink: 0 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m5 12 4 4L19 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </Combobox.ItemIndicator>
            </Combobox.Item>}
          </Combobox.List>
          {footer}
        </Combobox.Popup>
      </Combobox.Positioner>
    </Combobox.Portal>
  </Combobox.Root>;
}
